<template>
  <div class="admin-container">
    <div class="admin-header">
      <h2>Rota Overview</h2>
      <div class="admin-nav">
        <router-link to="/admin" class="nav-button">📅 Events</router-link>
        <router-link to="/admin_comics" class="nav-button">🎭 Comics</router-link>
        <router-link to="/admin_dates" class="nav-button">📊 Events</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

    <section>
      <div class="section-header">
        <h3>MC &amp; Admin/Tech Coverage</h3>
        <div class="filter-group">
          <div class="venue-filter">
            <label for="venue-select">Venue:</label>
            <select id="venue-select" v-model="selectedVenue" class="venue-select">
              <option value="all">All Venues</option>
              <option v-for="venue in venues" :key="venue.id" :value="venue.id">
                {{ venue.venue_name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Stats -->
      <div class="rota-stats">
        <div class="stat-card" :class="{ 'stat-ok': needsMcCount === 0 }">
          <div class="stat-number">{{ needsMcCount }}</div>
          <div class="stat-label">Need MC</div>
        </div>
        <div class="stat-card" :class="{ 'stat-ok': needsAdminTechCount === 0 }">
          <div class="stat-number">{{ needsAdminTechCount }}</div>
          <div class="stat-label">Need Admin/Tech</div>
        </div>
        <div class="stat-card stat-ok">
          <div class="stat-number">{{ fullyStaffedCount }}</div>
          <div class="stat-label">Fully Staffed</div>
        </div>
      </div>

      <div v-if="isLoading" class="loading">Loading rota data...</div>

      <div v-else-if="rotaEvents.length === 0" class="no-events">
        No upcoming events found.
      </div>

      <table v-else class="rota-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Venue</th>
            <th>MC</th>
            <th>Admin/Tech</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="event in rotaEvents" :key="event.id" class="rota-row">
            <td class="date-cell">{{ formatDate(event.event_date) }}</td>
            <td><span class="venue-badge">{{ event.venues?.venue_name }}</span></td>
            <td>
              <span v-if="event.mcAssigned" class="assigned">{{ event.mcAssigned }}</span>
              <span v-else class="needs-assignment">needs MC</span>
            </td>
            <td>
              <span v-if="event.adminTechAssigned" class="assigned">{{ event.adminTechAssigned }}</span>
              <span v-else class="needs-assignment">needs Admin/Tech</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { logoutAdmin } from '../utils/auth.js';

export default {
  name: 'AdminRotaPage',
  data() {
    return {
      events: [],
      venues: [],
      selectedVenue: 'all',
      isLoading: true,
    };
  },
  computed: {
    rotaEvents() {
      let filtered = this.events;

      if (this.selectedVenue !== 'all') {
        filtered = filtered.filter(e => e.venue_id == this.selectedVenue);
      }

      return filtered.map(event => {
        const apps = event.applications || [];

        const mcApp = apps.find(a =>
          a.slot_type === 'mc' && (a.status === 'confirmed' || a.status === 'offered')
        );
        const adminTechApp = apps.find(a =>
          a.slot_type === 'admin_tech' && (a.status === 'confirmed' || a.status === 'offered')
        );

        return {
          ...event,
          mcAssigned: mcApp ? mcApp.comedian_name : null,
          adminTechAssigned: adminTechApp ? adminTechApp.comedian_name : null,
        };
      }).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    },

    needsMcCount() {
      return this.rotaEvents.filter(e => !e.mcAssigned).length;
    },
    needsAdminTechCount() {
      return this.rotaEvents.filter(e => !e.adminTechAssigned).length;
    },
    fullyStaffedCount() {
      return this.rotaEvents.filter(e => e.mcAssigned && e.adminTechAssigned).length;
    },
  },
  async created() {
    await this.loadVenues();
    await this.loadEvents();
  },
  methods: {
    async loadVenues() {
      try {
        const { data, error } = await supabase
          .from('venues')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        this.venues = data || [];
      } catch (err) {
        console.error('Error loading venues:', err);
      }
    },

    async loadEvents() {
      this.isLoading = true;
      try {
        const today = new Date().toISOString().split('T')[0];

        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select(`
            *,
            venues (id, venue_name, venue_slug)
          `)
          .gte('event_date', today)
          .order('event_date', { ascending: true })
          .limit(100);

        if (eventsError) throw eventsError;

        if (!events || events.length === 0) {
          this.events = [];
          return;
        }

        const eventIds = events.map(e => e.id);

        const { data: applications, error: appsError } = await supabase
          .from('applications')
          .select('*')
          .in('event_id', eventIds);

        if (appsError) {
          console.error('Error fetching applications:', appsError);
        }

        const appsByEvent = {};
        (applications || []).forEach(app => {
          if (!appsByEvent[app.event_id]) appsByEvent[app.event_id] = [];
          appsByEvent[app.event_id].push(app);
        });

        this.events = events.map(event => ({
          ...event,
          applications: appsByEvent[event.id] || [],
        }));
      } catch (err) {
        console.error('Error loading events:', err);
        Swal.fire('Error', 'Failed to load rota data', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    logout() {
      logoutAdmin();
      this.$router.push('/');
    },
  }
};
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.admin-header h2 { margin: 0; color: #333; }
.admin-nav { display: flex; gap: 10px; align-items: center; }
.nav-button {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s;
}
.nav-button:hover { background: #5a6268; }
.nav-button.router-link-active { background: #007bff; }
.logout-button {
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.logout-button:hover { background: #c82333; }

section {
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h3 { margin-top: 0; margin-bottom: 15px; color: #34495e; }

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}
.filter-group { display: flex; gap: 15px; flex-wrap: wrap; align-items: center; }
.venue-filter { display: flex; align-items: center; gap: 8px; }
.venue-filter label { font-weight: 600; color: #666; font-size: 13px; }
.venue-select {
  padding: 6px 10px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  background: white;
  cursor: pointer;
}
.venue-select:focus { outline: none; border-color: #007bff; }

.rota-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}
.stat-card {
  text-align: center;
  padding: 15px;
  background: #fff3cd;
  border-radius: 8px;
  border: 1px solid #ffc107;
}
.stat-card.stat-ok {
  background: #d4edda;
  border-color: #28a745;
}
.stat-number { font-size: 28px; font-weight: bold; color: #333; }
.stat-label { font-size: 13px; color: #666; margin-top: 4px; }

.rota-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.rota-table th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
  font-size: 13px;
}
.rota-row td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.rota-row:hover { background: #f8f9fa; }

.date-cell { font-weight: 600; color: #007bff; white-space: nowrap; }

.venue-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.assigned {
  color: #155724;
  font-weight: 600;
  background: #d4edda;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 13px;
}

.needs-assignment {
  color: #856404;
  font-weight: 600;
  background: #fff3cd;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 13px;
}

.loading { text-align: center; padding: 40px; color: #666; }
.no-events { text-align: center; padding: 40px; color: #666; }

@media (max-width: 768px) {
  .admin-header { flex-direction: column; gap: 15px; text-align: center; }
  .admin-nav { flex-wrap: wrap; justify-content: center; }
  .rota-stats { grid-template-columns: 1fr; }
  .rota-table { font-size: 12px; }
}
</style>
